import { Modal, Stack, Select, Group, Button, Text } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { useState } from "react";

export type ReportType = "capital-gains" | "holdings" | "all-transactions";

const REPORT_TYPE_OPTIONS: { value: ReportType; label: string }[] = [
  { value: "capital-gains", label: "Capital Gain Report" },
  { value: "holdings", label: "Holding Statement" },
  { value: "all-transactions", label: "All Transactions Report" },
];

const SINCE_INCEPTION = "since-inception";

/** Indian FY start-year for a date (FY runs 1-Apr → 31-Mar) */
export const fyStartYear = (d: Date) => (d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear());

const fyLabel = (startYear: number) => `FY ${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;

interface ReportsModalProps {
  opened: boolean;
  onClose: () => void;
  onDownload: (type: ReportType, startYear?: number) => void | Promise<void>;
  isLoading?: boolean;
  /** Earliest FY start-year to offer in the picker (e.g. the user's signup FY) */
  earliestFyStartYear: number;
}

export default function ReportsModal({ opened, onClose, onDownload, isLoading = false, earliestFyStartYear }: ReportsModalProps) {
  const currentFy = fyStartYear(new Date());
  const [reportType, setReportType] = useState<ReportType>("capital-gains");
  const [fy, setFy] = useState<string>(String(currentFy));

  const fyOptions: { value: string; label: string }[] = [];
  if (reportType === "all-transactions") {
    fyOptions.push({ value: SINCE_INCEPTION, label: "Since inception" });
  }
  for (let y = currentFy; y >= Math.min(earliestFyStartYear, currentFy); y--) {
    fyOptions.push({ value: String(y), label: fyLabel(y) });
  }

  const handleDownload = async () => {
    if (reportType === "holdings") {
      await onDownload(reportType);
      return;
    }
    await onDownload(reportType, fy === SINCE_INCEPTION ? undefined : Number(fy));
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} size="lg">
          Download Report
        </Text>
      }
      centered
      size="md"
    >
      <Stack gap="md">
        <Select
          label="Report type"
          data={REPORT_TYPE_OPTIONS}
          value={reportType}
          onChange={(v) => {
            if (!v) return;
            setReportType(v as ReportType);
            // "Since inception" only exists for the transactions report
            if (v !== "all-transactions" && fy === SINCE_INCEPTION) setFy(String(currentFy));
          }}
          allowDeselect={false}
        />

        {reportType !== "holdings" ? (
          <Select
            label="Financial year"
            data={fyOptions}
            value={fy}
            onChange={(v) => v && setFy(v)}
            allowDeselect={false}
            searchable={fyOptions.length > 8}
          />
        ) : (
          <Text size="sm" c="dimmed">
            Holding statement is generated as on today, using the latest available NAVs.
          </Text>
        )}

        <Group justify="flex-end" mt="md">
          <Button
            color="primary.6"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownload}
            loading={isLoading}
          >
            Download
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
